<template>
  <template v-if="pending.length">
    <!--
      Lives in the header, which is rendered on every screen and never
      scrolls - so the prompt is there whether you're on the menu, in a
      lobby, or mid-game. Matt: "I should see that approval banner on any of
      the pages whether I'm playing in a specific game or on the home screen.
      I should be prompted all the time for that."
    -->
    <button class="approval-chip" :aria-expanded="isOpen" @click="isOpen = !isOpen">
      <span class="approval-chip__count">{{ pending.length }}</span>
      waiting
    </button>

    <!--
      Teleported so the expanded list isn't clipped by the header, which is a
      short flex row with its own stacking context.
    -->
    <Teleport to="body">
      <div v-if="isOpen" class="approval-backdrop" @click.self="isOpen = false">
        <div class="approval-panel" role="dialog" aria-label="People waiting for access">
          <h2>Waiting for access</h2>
          <p class="approval-panel__note">
            Approving somebody lets them into every Around Table Round game,
            not just this one.
          </p>
          <div v-for="person in pending" :key="person.uid" class="approval-row">
            <span class="approval-row__who">
              <strong>{{ person.displayName || person.email }}</strong>
              <small v-if="person.displayName">{{ person.email }}</small>
            </span>
            <button class="approval-row__yes" @click="setStatus(person.uid, 'approved')">Approve</button>
            <button class="approval-row__no" @click="setStatus(person.uid, 'denied')">Deny</button>
          </div>
          <button class="secondary" @click="isOpen = false">Close</button>
        </div>
      </div>
    </Teleport>
  </template>
</template>

<script>
import { watchAllSiteUsers, setSiteUserStatus } from '@/store/siteAccess';

// The approval prompt, shown to any approved admin. There is still no email
// notification for new sign-ups - that needs the Firebase project on the paid
// plan plus a Cloud Function - so this chip is the only prompt there is,
// which is exactly why it has to be somewhere you cannot miss it.
export default {
  name: 'AdminPanel',
  data() {
    return { people: [], unwatch: null, isOpen: false };
  },
  computed: {
    pending() { return this.people.filter((p) => p.status === 'pending'); },
  },
  mounted() {
    // watchAllSiteUsers hands back the raw RTDB node: an OBJECT keyed by uid,
    // not an array. The first version of this assigned it straight to
    // `people` and then called .filter on it, which throws - so the panel
    // rendered nothing at all and there was no way to approve anyone from
    // this app. The sibling repos convert it in a Vuex getter; this one does
    // it here.
    this.unwatch = watchAllSiteUsers((users) => {
      this.people = Object.entries(users || {}).map(([uid, u]) => ({ uid, ...u }));
    });
  },
  beforeUnmount() {
    if (this.unwatch) this.unwatch();
  },
  methods: {
    setStatus(uid, status) {
      setSiteUserStatus(uid, status);
      // Closing on the last one avoids leaving an empty dialog open.
      if (this.pending.length <= 1) this.isOpen = false;
    },
  },
};
</script>

<style scoped>
.approval-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0.25rem 0.6rem;
  min-height: 32px;
  border-radius: 2rem;
  border: 1px solid #a8862f;
  background: #e8c86a;
  color: #211a05;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.approval-chip__count {
  display: inline-grid;
  place-items: center;
  min-width: 17px;
  height: 17px;
  border-radius: 50%;
  background: #211a05;
  color: #e8c86a;
  font-size: 0.68rem;
}

.approval-backdrop {
  position: fixed;
  inset: 0;
  z-index: 960;
  background: rgba(0, 0, 0, 0.65);
  display: grid;
  place-items: center;
  padding: 1.25rem;
}

.approval-panel {
  width: 100%;
  max-width: 440px;
  max-height: 80vh;
  overflow-y: auto;
  padding: 1.2rem 1.3rem;
  border-radius: 14px;
  background: #22312a;
  border: 1px solid #a8862f;
  color: #f2f5f3;
}

.approval-panel h2 { margin: 0 0 0.4rem; font-size: 1.15rem; }

.approval-panel__note {
  margin: 0 0 1rem;
  font-size: 0.78rem;
  color: #b9c6bf;
  line-height: 1.5;
}

.approval-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.5rem 0;
  border-top: 1px solid #3a4d43;
}

.approval-row__who {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.approval-row__who small {
  font-size: 0.7rem;
  color: #b9c6bf;
  overflow: hidden;
  text-overflow: ellipsis;
}

.approval-row__yes,
.approval-row__no {
  padding: 0.35rem 0.7rem;
  min-height: 36px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  border: 1px solid #3a4d43;
}

.approval-row__yes { background: #4f9d6e; color: #06150d; }
.approval-row__no { background: transparent; color: #b9c6bf; }

.approval-panel .secondary {
  width: 100%;
  margin-top: 0.9rem;
  padding: 0.7rem;
  border-radius: 12px;
  border: 1px solid #3a4d43;
  color: #b9c6bf;
  font-weight: 600;
}
</style>
